import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import { Download, ScrollText } from "lucide-react";
import {
  Button,
  DataTable,
  EmptyState,
  Pagination,
  SettingsGroup,
  useCursorPagination,
} from "@playbookai/ui";
import { AdminSection } from "./AdminSection.js";
import { useQuery } from "@tanstack/react-query";
import { useInRouterContext } from "react-router-dom";
import { useAuthStore } from "../../auth/auth-store.js";
import { scopeFromClaims } from "../../auth/session-scope.js";
import { useAuditExportDownload } from "../../store/audit-export-download.js";
import { AuditExportAutoDownload } from "./audit/AuditExportAutoDownload.js";
import { AuditExportLinkHandler } from "./audit/AuditExportLinkHandler.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import {
  auditQueryKeys,
  createAuditExport,
  getAuditFacets,
  searchAuditEvents,
  type AuditEvent,
} from "../../api/admin-audit-api.js";
import {
  dayGroups,
  EMPTY_FILTERS,
  isNarrowed,
  keepsPreviousPage,
  PAGE_SIZE,
  rangeError,
  toApiFilters,
  unboundedFilterError,
  type ExportState,
  type FilterDraft,
} from "./audit/audit-query.js";
import { auditColumns } from "./audit/audit-columns.js";
import { EventDrawer } from "./audit/EventDrawer.js";
import { AuditFilterBar } from "./audit/AuditFilterBar.js";
import { AuditLearnMore } from "./audit/AuditLearnMore.js";

/* `PAGE_SIZE`, `rangeError` and `unboundedFilterError` moved to
   `audit/audit-query.ts` with the rest of the filter and paging rules, and are
   re-exported here so their one existing consumer — the page's own spec, which
   asserts them directly without mounting a calendar or a table — keeps
   importing them from the page it is a spec for. */
export { PAGE_SIZE, rangeError, unboundedFilterError };

/* Audit & compliance admin page (PB-42 / PB-1588 domain 1).

   Read-only by construction: `audit.audit_log` has UPDATE/DELETE revoked at
   the database level (ADR-0005), so there is no state this page could change
   even in principle — and therefore nothing here to audit in turn.

   One complete history: access grants and removals are audit events, so they
   belong in the same newest-first ledger as every other workspace change.
   Splitting the same underlying record into two tabs made the curated access
   subset look like a second source of truth and made exports harder to reason
   about. The `Source` label keeps each event's writer explicit without adding
   another navigation layer.

   The `/admin` route tree is gated once at its parent by RequireTenantAdmin
   (PB-48), so this page only mounts for a tenant admin; the server is still
   the authoritative gate on the audit endpoint. */

const EXPORT_STARTED_VISIBLE_MS = 5_000;

export function AuditAdmin(): ReactElement {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTERS);
  /* Applied separately from the draft: a filter change must start a new page
     set, and refetching the audit record on every keystroke would issue a
     query per character against an unbounded table. */
  const [applied, setApplied] = useState<FilterDraft>(EMPTY_FILTERS);
  const tenantId = useAuthStore((s) => s.claims?.tenantId);
  /* The bell/email link needs the router; the page's own unit spec renders it
     without one, and every other part of the page works there as before. */
  const routed = useInRouterContext();
  const [rangeMessage, setRangeMessage] = useState<string | null>(null);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [exportState, setExportState] = useState<ExportState>({
    status: "idle",
  });
  /* "Export started" only confirms the click: the download and the bell
     notification carry the rest, so the line clears itself. A failure stays. */
  useEffect(() => {
    if (exportState.status !== "queued") return;
    const timer = window.setTimeout(
      () => setExportState({ status: "idle" }),
      EXPORT_STARTED_VISIBLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [exportState]);
  /* The Event details drawer (PB-5216, d5). `selected` outlives `drawerOpen`
     so the panel keeps its rows through the close motion. */
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openEvent = useCallback((row: AuditEvent) => {
    setSelected(row);
    setDrawerOpen(true);
  }, []);
  const formatter = useDateTimeFormatter();
  const auditTable = useMemo(
    () => auditColumns(formatter, openEvent),
    [formatter, openEvent],
  );
  const groups = useMemo(() => dayGroups(formatter), [formatter]);

  const filters = toApiFilters(applied, formatter.timeZone);
  /* Two questions, answered separately (PB-5410). `narrowed` drives the empty
     state — did a filter exclude everything? — and the switch narrows nothing.
     `canClear` drives the Clear button, which also has to undo the switch, so
     it counts the switch once it differs from its default. */
  const narrowed = isNarrowed(applied);
  const canClear = narrowed || applied.includeRoutine;
  /* Whether the switch is actually hiding rows: the server ignores it beside an
     explicit Action, so the empty state must too. */
  const routineHidden = !applied.includeRoutine && applied.action === "";

  /* Fetched once and cached: the option lists change only when a service
     records an action nobody has recorded before, which is rare. */
  const facets = useQuery({
    queryKey: auditQueryKeys.facets(tenantId),
    queryFn: getAuditFacets,
  });

  const auditPaging = useCursorPagination(
    `${tenantId ?? "none"}|${JSON.stringify(filters)}`,
    { pageSize: PAGE_SIZE },
  );
  const audit = useQuery({
    queryKey: [
      ...auditQueryKeys.search(tenantId, filters),
      auditPaging.cursor ?? "first",
    ],
    queryFn: () => searchAuditEvents(auditPaging.cursor, filters, PAGE_SIZE),
    /* Keep the previous page on screen only while the TENANT and the FILTERS
       both match. Matching on tenant alone let a filter change keep the old
       rows visible while the new query ran, and the table only shows its
       loading state on a first fetch, so those rows carried no indication
       they answered a different question. Cursor changes within one filter
       set still reuse them, which is what the placeholder is for. */
    placeholderData: (previousData, previousQuery) =>
      keepsPreviousPage(previousQuery?.queryKey, tenantId, filters)
        ? previousData
        : undefined,
  });

  async function onExport(): Promise<void> {
    /* Export what the form SHOWS, not what was last searched. `applied` moves
       only on submit, so a reader who edits the range or the service and then
       clicks Export without clicking Apply would otherwise receive a file
       built from the previous filters while the form displays the new ones —
       a compliance artefact they would go on to describe with the wrong scope.
       Applying the draft here also keeps the table and the file in agreement,
       and reuses the same range check Apply performs. */
    const problem =
      rangeError(draft.from, draft.to) ?? unboundedFilterError(draft);
    if (problem) {
      setRangeMessage(problem);
      return;
    }
    setRangeMessage(null);
    setApplied(draft);

    setExportState({ status: "working" });
    /* Captured BEFORE the request: the write carries the session that asked,
       so a session swap while it is in flight can never download it for the
       next administrator (session-scope.ts). */
    const origin = scopeFromClaims(useAuthStore.getState().claims);
    try {
      const job = await createAuditExport(
        toApiFilters(draft, formatter.timeZone),
      );
      /* No popup: this page downloads the CSV itself when the job is ready,
         and the bell notification covers everything else. */
      if (origin) useAuditExportDownload.getState().watch(origin, job.jobId);
      setExportState({
        status: "queued",
        /* The server's rule, not the switch alone: an explicit action
           overrides the switch, so an action exported by name hides nothing. */
        routineExcluded: !draft.includeRoutine && draft.action === "",
      });
    } catch (error) {
      setExportState({ status: "failed", message: (error as Error).message });
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    /* Belt and braces: `min`/`max` stop the picker offering an invalid value,
       but a typed one can still get through, and the server rejects a reversed
       range with a 400. Saying so here is kinder than surfacing that. */
    const problem =
      rangeError(draft.from, draft.to) ?? unboundedFilterError(draft);
    if (problem) {
      setRangeMessage(problem);
      return;
    }
    setRangeMessage(null);
    setApplied(draft);
  }

  function onClear(): void {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  }

  /* The switch applies at once (PB-5410 P-7), and ONLY the switch: the text
     fields' pending edits stay in the draft until Apply, as they always have.
     Changing the filters re-keys the paging, so the list starts from page 1. */
  function onIncludeRoutineChange(includeRoutine: boolean): void {
    setDraft((current) => ({ ...current, includeRoutine }));
    setApplied((current) => ({ ...current, includeRoutine }));
  }

  const auditRows = audit.data?.data ?? [];

  return (
    <AdminSection
      icon={ScrollText}
      title="Audit & compliance"
      description="Review every recorded workspace change, including membership and role updates."
      action={
        /* Grows from about the buttons' width, so the export status line
           wraps beside the title instead of pushing this whole block onto its
           own row, where it sat left-aligned. */
        <div className="flex min-w-0 grow basis-60 flex-col items-end gap-2">
          {/* Both render nothing. Adjacent, link handler FIRST: React runs one
              commit's effects in tree order, so a bell link for the job this
              page is already watching is claimed before the watcher looks. */}
          {routed ? (
            <AuditExportLinkHandler onExportState={setExportState} />
          ) : null}
          <AuditExportAutoDownload tenantId={tenantId} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setLearnMoreOpen(true)}
            >
              Learn more
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void onExport()}
              disabled={exportState.status === "working"}
            >
              <Download className="size-4" aria-hidden="true" />
              {exportState.status === "working" ? "Preparing…" : "Export CSV"}
            </Button>
          </div>
          {exportState.status === "queued" ? (
            <p
              role="status"
              className="max-w-xl text-right text-dense-1 text-pb-text-secondary"
            >
              Export started. The CSV downloads automatically when it&apos;s
              ready, and you&apos;ll get a notification.
              {exportState.routineExcluded
                ? " Routine activity excluded."
                : null}
            </p>
          ) : exportState.status === "failed" ? (
            <div
              role="alert"
              className="max-w-xl text-right text-dense-1 text-pb-red-text"
            >
              <p className="font-medium">Could not export the audit record</p>
              <p>{exportState.message}</p>
            </div>
          ) : null}
        </div>
      }
      meta={null}
    >
      <SettingsGroup
        title="Audit history"
        description="A single, append-only timeline of workspace, membership and role changes. Newest events appear first."
      >
        <AuditFilterBar
          draft={draft}
          setDraft={setDraft}
          facets={facets}
          formatter={formatter}
          canClear={canClear}
          rangeMessage={rangeMessage}
          onSubmit={onSubmit}
          onClear={onClear}
          onIncludeRoutineChange={onIncludeRoutineChange}
        />
        <DataTable
          columns={auditTable}
          layout="fixed"
          /* Inside the table: a failed load used to swap the whole
                     table for a card, so the columns and the filters above
                     them jumped. */
          error={
            audit.error
              ? {
                  title: "Couldn't load the audit record",
                  description: audit.error.message,
                  onRetry: () => {
                    void audit.refetch();
                  },
                }
              : undefined
          }
          data={auditRows}
          getRowId={(row) => row.id}
          textSize="small"
          nowrap
          groupBy={groups}
          onRowActivate={openEvent}
          loading={audit.isPending && audit.isFetching}
          pinFirstColumn
          caption="Tenant audit record"
          /* Three empty states, because zero rows means three different things
             (PB-5410). A filter excluded everything; or the default view, which
             hides routine activity, has nothing to show; or the record is truly
             empty. Each copy claims exactly what the rows support — "No audit
             events yet" under the default view would be false, and so would
             claiming routine activity exists, since a new workspace has none.
             Wherever the switch is hiding rows (an explicit Action overrides
             it), the copy says so rather than blaming the filters alone. */
          emptyState={
            narrowed ? (
              <EmptyState
                title="No events match these filters"
                description={
                  routineHidden
                    ? "Routine activity is hidden too. Widen the time range, clear the filters or turn on Include routine activity."
                    : "Widen the time range or clear the filters to see more events."
                }
              />
            ) : routineHidden ? (
              <EmptyState
                title="No events to show"
                description="Routine activity (connection tickets and integration runs) is hidden. Turn on Include routine activity to include it."
              />
            ) : (
              <EmptyState
                title="No audit events yet"
                description="Workspace and access changes will appear here as they happen."
              />
            )
          }
        />
        {auditPaging.page > 1 || audit.data?.hasMore ? (
          <div className="mt-3 flex justify-end" data-testid="audit-pagination">
            <Pagination
              mode="cursor"
              page={auditPaging.page}
              pageItemCount={auditRows.length}
              canGoBack={auditPaging.canGoBack}
              hasNext={Boolean(audit.data?.hasMore && audit.data.nextCursor)}
              busy={audit.isFetching}
              onPrev={auditPaging.goBack}
              onNext={() => {
                const cursor = audit.data?.nextCursor;
                if (cursor) auditPaging.goNext(cursor);
              }}
            />
          </div>
        ) : null}
      </SettingsGroup>

      <EventDrawer
        open={drawerOpen}
        event={selected}
        formatter={formatter}
        onClose={() => setDrawerOpen(false)}
      />
      <AuditLearnMore
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
      />
    </AdminSection>
  );
}
